import React, { useContext, useCallback } from 'react';
import get from 'lodash/get';
import { useSelector } from 'react-redux';
import { thresholdCellConfigSelector } from '@store/noc/threshold/selectors';
import { adminRoleSelector, nocModeSelector } from '@store/noc/base/selectors';
import { filterSelector as trendsViewFilter } from '@store/noc/trendsData/selectors';
import { filterSelector as filterViewFilters } from '@store/noc/filtersData/selectors';
import ThresholdInput from './ThresholdInput';
import { isEiModule, NOC_MODE_TRENDS } from '@utils/constants';
import ParentContext, { type ThresholdError } from '@src/pages/Noc/Threshold/context';
import { adKeys, ERROR_MSG, keys, percentageValidation, isInstantFilter } from '../utils/constants';

interface IProps {
  cellData?: {
    metric: { key: string; unit: string; metrics?: string[] };
    data: { conviva: string; custom: string; isCustomSelected: boolean };
    identifier?: string;
    unit?: string;
  };
  type: 'threshold_baseline' | 'thresholds';
  labelAttributes?: { prefix: string; suffix: string };
  hideConvivaInput: boolean;
  checkForCustom?: boolean;
}

function convertMetric(metricName: string) {
  return isEiModule ? keys[metricName].key : adKeys[metricName].key;
}

function useDisableCustom(
  cellData: Record<string, any> | undefined,
  disableThreshold: boolean,
  checkForCustom?: boolean,
) {
  const isAdmin = useSelector(adminRoleSelector);
  const mode = useSelector(nocModeSelector);
  const trendFilter = useSelector(trendsViewFilter);
  const filters = useSelector(filterViewFilters);
  const thresholdDisabled = !isAdmin ? true : disableThreshold;
  let disableCustom = false;
  if (checkForCustom && !thresholdDisabled) {
    const filter =
      mode === NOC_MODE_TRENDS ? trendFilter : filters.find((i) => i.name === cellData?.identifier);
    disableCustom = isInstantFilter(filter);
  }
  if (disableCustom) return 'instantFilter';
  if (!isAdmin) return 'notAdmin';
  if (thresholdDisabled) return 'disableThreshold';
  return '';
}

function useErrorMsg(type: IProps['type'], cellData: IProps['cellData'], error: ThresholdError) {
  const errorItem =
    type === 'threshold_baseline' ? error[type] : error[type][cellData?.identifier || ''];
  const errorKeys = cellData?.metric.metrics?.map((i) => convertMetric(i)) || [
    cellData?.metric.key,
  ];
  const msgKey = errorKeys.find((key) => errorItem?.[key || '']);
  return ERROR_MSG[errorItem?.[msgKey || '']];
}

const Cell = ({ labelAttributes, cellData, checkForCustom, type: cellType }: IProps) => {
  const { error, thresholdConfig, handleThresholdUpdate } = useContext(ParentContext);
  const cellConfigStore = useSelector(thresholdCellConfigSelector);
  const disableCustomType = useDisableCustom(
    cellData,
    cellConfigStore.thresholdDisabled,
    checkForCustom,
  );
  const currentData = { ...cellData };

  let path;
  if (cellType === 'threshold_baseline') {
    const key = cellData
      ? (cellData.metric.metrics || [cellData.metric.key]).find(
          (i) => i in thresholdConfig.threshold_baseline,
        )
      : '';
    path = ['threshold_baseline', key];
  } else {
    path = ['thresholds', cellData?.identifier, cellData?.metric.key];
  }
  const data = get(thresholdConfig, path);
  if (data) {
    currentData.data = { ...cellData?.data, ...data };
  }

  const errorMsg = useErrorMsg(cellType, cellData, error);

  const handleChange = useCallback(
    ({ metric, config, type }) => {
      if (metric.metrics) {
        const configs = metric.metrics.map((metricName) => {
          const identifier = convertMetric(metricName);
          return {
            ...config,
            identifier,
          };
        });
        handleThresholdUpdate({ type, configs });
      } else {
        const validate = metric.unit === '%' ? percentageValidation : undefined;
        handleThresholdUpdate({ type, configs: [config], validate });
      }
    },
    [handleThresholdUpdate],
  );

  return (
    <ThresholdInput
      type={cellType}
      cellData={currentData}
      labelAttributes={labelAttributes}
      handleChange={handleChange}
      disableCustom={disableCustomType}
      error={errorMsg}
    />
  );
};

export default Cell;
